import { FileConversionService, Select, UploadFile, BankFileConversionRail } from 'vopay';
import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldData } from '@/components/Form/types';
import * as Yup from 'yup';
import { useFileConfigurations } from '../FileConfigurationProvider';

export type ConvertMyTemplateFormValues = {
  Service: FileConversionService | '';
  FileBase64: string;
  FileName: string;
  PaymentRail?: BankFileConversionRail | '';
  ValueDate?: string;
  FileConversionConfigurationID?: string;
};

export const useConvertMyTemplateFormFields = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.FileUploadDrawer.ConvertTemplate' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { configurations, configurationsLoading } = useFileConfigurations();

  const hasOnlyDefaultProcessor = useMemo(() => {
    return configurations.length === 1 && configurations[0].Processor === 'default_processor';
  }, [configurations]);

  const serviceOptions = useMemo(() => {
    let hasCredit = false;
    let hasDebit = false;

    if (configurations) {
      Object.values(configurations).forEach((config) => {
        if (config.PaymentRails?.credit && Object.keys(config.PaymentRails.credit).length > 0) {
          hasCredit = true;
        }
        if (config.PaymentRails?.debit && Object.keys(config.PaymentRails.debit).length > 0) {
          hasDebit = true;
        }
      });
    }

    if (!hasCredit && !hasDebit) {
      hasCredit = true;
      hasDebit = true;
    }

    const options = [];
    if (hasDebit) {
      options.push({ value: 'debit', label: tGlobal('ServiceOptions.debit') });
    }
    if (hasCredit) {
      options.push({ value: 'credit', label: tGlobal('ServiceOptions.credit') });
    }

    return options;
  }, [configurations]);

  const uploadFile: FieldData<ConvertMyTemplateFormValues>[] = [
    {
      fieldName: 'Service',
      label: () => t('serviceLabel', { defaultValue: 'Transfer Type' }),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const data = getFormikData<ConvertMyTemplateFormValues['Service']>({});

        // Set the value automatically if there's only one option
        useEffect(() => {
          if (serviceOptions.length === 1 && !ctx.values.Service) {
            ctx.setFieldValue('Service', serviceOptions[0].value);
          }
        }, [serviceOptions, ctx.values.Service]);

        return (
          <Select
            version="2"
            {...data}
            options={serviceOptions}
            placeholder=""
            sort={false}
            disabled={data.disabled || serviceOptions.length === 1}
            loading={configurationsLoading}
          />
        );
      },
    },
    {
      fieldName: 'FileBase64',
      label: () => t('fileLabel', { defaultValue: 'Attachment' }),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const data = getFormikData<ConvertMyTemplateFormValues['FileBase64']>({});
        return (
          <UploadFile
            id="FileBase64"
            {...data}
            variant="onboarding"
            onBlurFileName={(e: React.FocusEvent<HTMLInputElement>) => ctx.handleBlur(e)}
            onChangeFileName={(e: React.ChangeEvent<HTMLInputElement>) => ctx.setFieldValue('FileName', e.target.value)}
            idFileName="FileName"
            valueFileName={ctx.values.FileName}
            maxMBSize={20}
            keepExtension={true}
          />
        );
      },
    },
  ];

  return { uploadFile, hasOnlyDefaultProcessor };
};
